import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, Save, Plus, BookOpen, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SCHULUNGSINHALTE = ['Kompressionstherapie', 'Druckentlastung', 'Ernährung', 'Hautpflege', 'Verbandwechsel', 'Bewegungsförderung', 'Schmerzbewältigung', 'Wundbeobachtung'];

export default function BeratungPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [beratungen, setBeratungen] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    patient_id: patientId, datum: new Date().toISOString().slice(0, 10),
    thema: '', inhalt: '', teilnehmer: '',
    krankheitsverstaendnis_freitext: '', krankheitsverstaendnis_skala: 3,
    selbstmanagement_skala: 3,
    schulungsinhalte: [], schulungsinhalte_sonstige: '',
    evaluation_verstanden: '', evaluation_bemerkung: ''
  });

  useEffect(() => { loadBeratungen(); }, [patientId]);

  const loadBeratungen = async () => {
    try {
      const token = localStorage.getItem('wunddok_token');
      const res = await axios.get(`${API}/api/beratungen/${patientId}`, { headers: { Authorization: `Bearer ${token}` } });
      setBeratungen(res.data);
    } catch { /* no beratungen */ }
    finally { setLoading(false); }
  };

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleList = (k, item) => setForm(p => {
    const l = p[k] || [];
    return { ...p, [k]: l.includes(item) ? l.filter(i => i !== item) : [...l, item] };
  });

  const handleSave = async () => {
    if (!form.thema) { toast.error('Bitte ein Thema angeben'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('wunddok_token');
      await axios.post(`${API}/api/beratungen`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Beratung dokumentiert');
      setShowForm(false);
      setForm({ patient_id: patientId, datum: new Date().toISOString().slice(0, 10), thema: '', inhalt: '', teilnehmer: '', krankheitsverstaendnis_freitext: '', krankheitsverstaendnis_skala: 3, selbstmanagement_skala: 3, schulungsinhalte: [], schulungsinhalte_sonstige: '', evaluation_verstanden: '', evaluation_bemerkung: '' });
      loadBeratungen();
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const formatDate = d => { try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; } };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="beratung-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 rounded-xl" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1"><h1 className="text-xl font-heading font-bold text-slate-900">Beratung & Schulung</h1></div>
      </div>

      <Button onClick={() => setShowForm(!showForm)} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="new-beratung-btn">
        <Plus className="w-4 h-4 mr-2" />Neue Beratung dokumentieren
      </Button>

      {showForm && (
        <div className="space-y-4 animate-fade-in">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><BookOpen className="w-5 h-5 text-teal-700" />Beratungsgespräch</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Datum</Label><Input type="date" value={form.datum} onChange={e => update('datum', e.target.value)} className="h-12" data-testid="beratung-datum" /></div>
                <div className="space-y-2"><Label>Teilnehmer</Label><Input value={form.teilnehmer} onChange={e => update('teilnehmer', e.target.value)} placeholder="Patient, Angehörige..." className="h-12" data-testid="beratung-teilnehmer" /></div>
              </div>
              <div className="space-y-2"><Label>Thema *</Label>
                <Select value={form.thema} onValueChange={v => update('thema', v)}>
                  <SelectTrigger className="h-12" data-testid="beratung-thema"><SelectValue placeholder="Thema auswählen" /></SelectTrigger>
                  <SelectContent>{['Krankheitsbild & Ursachen', 'Wundversorgung', 'Kompressionstherapie', 'Druckentlastung', 'Ernährung', 'Hautpflege', 'Schmerzmanagement', 'Mobilisation', 'Hilfsmittel', 'Sonstiges'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Gesprächsinhalt</Label>
                <Textarea value={form.inhalt} onChange={e => update('inhalt', e.target.value)} placeholder="Inhalt des Beratungsgesprächs..." rows={3} data-testid="beratung-inhalt" /></div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Krankheitsverständnis & Selbstmanagement</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label>Krankheitsverständnis (Freitext)</Label>
                <Textarea value={form.krankheitsverstaendnis_freitext} onChange={e => update('krankheitsverstaendnis_freitext', e.target.value)} placeholder="Was hat der Patient verstanden?" rows={2} data-testid="beratung-kv-freitext" /></div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label>Krankheitsverständnis (1-5)</Label><span className="text-sm font-bold text-teal-700">{form.krankheitsverstaendnis_skala}/5</span></div>
                <Slider value={[form.krankheitsverstaendnis_skala]} onValueChange={([v]) => update('krankheitsverstaendnis_skala', v)} min={1} max={5} step={1} data-testid="beratung-kv-skala" />
                <div className="flex justify-between text-xs text-slate-400"><span>Kein Verständnis</span><span>Volles Verständnis</span></div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label>Selbstmanagement-Fähigkeiten (1-5)</Label><span className="text-sm font-bold text-teal-700">{form.selbstmanagement_skala}/5</span></div>
                <Slider value={[form.selbstmanagement_skala]} onValueChange={([v]) => update('selbstmanagement_skala', v)} min={1} max={5} step={1} data-testid="beratung-sm-skala" />
                <div className="flex justify-between text-xs text-slate-400"><span>Nicht möglich</span><span>Vollständig</span></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Schulungsinhalte</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {SCHULUNGSINHALTE.map(o => (
                  <button key={o} type="button" onClick={() => toggleList('schulungsinhalte', o)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border min-h-[44px] transition-all ${form.schulungsinhalte.includes(o) ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-slate-200 text-slate-600'}`}
                    data-testid={`schulung-${o.replace(/\s/g, '-').toLowerCase()}`}>{o}</button>
                ))}
              </div>
              <div className="space-y-2"><Label>Sonstige Inhalte</Label>
                <Input value={form.schulungsinhalte_sonstige} onChange={e => update('schulungsinhalte_sonstige', e.target.value)} placeholder="Weitere Schulungsinhalte..." className="h-12" data-testid="schulung-sonstige" /></div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Evaluation</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label>Hat der Patient die Inhalte verstanden?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[['ja', 'bg-emerald-50 border-emerald-300 text-emerald-700'], ['teilweise', 'bg-amber-50 border-amber-300 text-amber-700'], ['nein', 'bg-rose-50 border-rose-300 text-rose-700']].map(([v, cls]) => (
                    <button key={v} type="button" onClick={() => update('evaluation_verstanden', v)}
                      className={`p-3 rounded-xl text-sm font-medium border min-h-[44px] transition-all ${form.evaluation_verstanden === v ? cls : 'bg-white border-slate-200 text-slate-500'}`}
                      data-testid={`eval-verstanden-${v}`}>{v}</button>
                  ))}
                </div></div>
              <div className="space-y-2"><Label>Bemerkungen zur Evaluation</Label>
                <Textarea value={form.evaluation_bemerkung} onChange={e => update('evaluation_bemerkung', e.target.value)} placeholder="z.B. Wiederholung nötig..." rows={2} data-testid="eval-bemerkung" /></div>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="save-beratung-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Speichern...' : 'Beratung speichern'}
          </Button>
        </div>
      )}

      {/* Beratungsliste */}
      <div className="space-y-2">
        {loading ? <div className="h-20 bg-slate-100 rounded-2xl animate-pulse" /> :
          beratungen.length === 0 && !showForm ? (
            <div className="text-center py-8 text-slate-400"><BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Noch keine Beratungen dokumentiert</p></div>
          ) : beratungen.map(b => (
            <Card key={b.id} className="border border-slate-200 shadow-sm" data-testid={`beratung-card-${b.id}`}>
              <CardContent className="p-0">
                <button className="w-full p-4 text-left flex items-start gap-3" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>
                  <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-teal-700" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><p className="text-sm font-medium text-slate-900">{b.thema}</p>
                      {b.evaluation_verstanden && <Badge variant="outline" className={`text-[10px] border ${b.evaluation_verstanden === 'ja' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : b.evaluation_verstanden === 'teilweise' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>{b.evaluation_verstanden}</Badge>}</div>
                    <p className="text-xs text-slate-500 mt-0.5">{formatDate(b.datum)} &middot; {b.teilnehmer || 'Patient'}</p>
                  </div>
                  {expanded === b.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {expanded === b.id && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2 animate-fade-in text-sm">
                    {b.inhalt && <div><p className="text-xs text-slate-400 uppercase">Inhalt</p><p className="text-slate-600">{b.inhalt}</p></div>}
                    {b.krankheitsverstaendnis_freitext && <div><p className="text-xs text-slate-400 uppercase">Krankheitsverständnis</p><p className="text-slate-600">{b.krankheitsverstaendnis_freitext}</p></div>}
                    <div className="flex gap-4">
                      <span className="text-slate-500">KV: <span className="font-medium text-slate-700">{b.krankheitsverstaendnis_skala}/5</span></span>
                      <span className="text-slate-500">SM: <span className="font-medium text-slate-700">{b.selbstmanagement_skala}/5</span></span>
                    </div>
                    {b.schulungsinhalte?.length > 0 && <div><p className="text-xs text-slate-400 uppercase">Schulung</p><p className="text-slate-600">{b.schulungsinhalte.join(', ')}</p></div>}
                    {b.evaluation_bemerkung && <div><p className="text-xs text-slate-400 uppercase">Evaluation</p><p className="text-slate-600">{b.evaluation_bemerkung}</p></div>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
}
